import { useState } from 'react';
import Layout from '../components/Layout';
import { api } from '../services/api';
import RewriteModal from '../components/RewriteModal';

interface BrandBrainResult {
  analysis: string;
  brand_name: string;
  input_tokens: number;
  output_tokens: number;
}

export default function BrandBrainPage() {
  const [brandName, setBrandName] = useState('');
  const [focusArea, setFocusArea] = useState('full');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<BrandBrainResult | null>(null);
  const [editedContent, setEditedContent] = useState('');
  const [copied, setCopied] = useState(false);
  const [selectedText, setSelectedText] = useState('');
  const [showRewriteModal, setShowRewriteModal] = useState(false);

  const handleTextSelect = (e: React.SyntheticEvent<HTMLTextAreaElement>) => {
    const ta = e.currentTarget;
    setSelectedText(ta.value.substring(ta.selectionStart, ta.selectionEnd));
  };

  const handleRewriteAccept = (rewrittenText: string) => {
    setEditedContent(editedContent.replace(selectedText, rewrittenText));
    setShowRewriteModal(false);
    setSelectedText('');
  };

  const handleAnalyze = async () => {
    if (!brandName.trim()) {
      setError('Please enter a brand name');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setResult(null);
      setEditedContent('');

      const res = await api.post('/brandbrain/analyze', {
        brand_name: brandName.trim(),
        focus_area: focusArea,
      });

      setResult(res.data);
      setEditedContent(res.data.analysis);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Analysis failed');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    await navigator.clipboard.writeText(editedContent);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Brand Brain</h1>
          <p className="text-gray-500 mt-1">
            Generate a comprehensive 360° intelligence report on any brand — market positioning, sentiment, competitive landscape, and more
          </p>
        </div>

        {/* Input Section */}
        <div className="bg-white rounded-xl border shadow-sm p-6 mb-6">
          <div className="space-y-4">
            {/* Brand Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Brand Name</label>
              <input
                type="text"
                value={brandName}
                onChange={(e) => setBrandName(e.target.value)}
                placeholder="e.g., Notion, Figma, HubSpot, Salesforce..."
                className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                onKeyDown={(e) => e.key === 'Enter' && !loading && handleAnalyze()}
              />
            </div>

            {/* Focus Area */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Focus Area</label>
              <select
                value={focusArea}
                onChange={(e) => setFocusArea(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              >
                <option value="full">Full 360° Report</option>
                <option value="positioning">Market Positioning</option>
                <option value="sentiment">User Sentiment</option>
                <option value="competitive">Competitive Insights</option>
              </select>
            </div>

            <button
              onClick={handleAnalyze}
              disabled={loading || !brandName.trim()}
              className="w-full py-3 rounded-lg bg-brand-600 text-white font-medium hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Analyzing {brandName}...
                </>
              ) : (
                'Analyze Brand'
              )}
            </button>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-xl p-4">
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}

        {/* Result */}
        {result && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            {/* Toolbar */}
            <div className="flex items-center justify-between px-6 py-3 border-b border-gray-200">
              <div>
                <h3 className="font-medium text-gray-900">Brand Report: {result.brand_name}</h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  Tokens: {result.input_tokens?.toLocaleString()} in / {result.output_tokens?.toLocaleString()} out
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowRewriteModal(true)}
                  disabled={!selectedText}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-purple-300 text-purple-700 hover:bg-purple-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  Rewrite
                </button>
                <button
                  onClick={handleCopy}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors"
                >
                  {copied ? (
                    <>
                      <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-green-600">Copied!</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                      Copy
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              <textarea
                value={editedContent}
                onChange={(e) => setEditedContent(e.target.value)}
                onSelect={handleTextSelect}
                className="w-full min-h-[600px] rounded-lg border border-gray-300 px-4 py-3 text-sm font-mono leading-relaxed focus:ring-2 focus:ring-brand-500 focus:border-brand-500 resize-y"
                spellCheck={false}
              />
            </div>
          </div>
        )}
      </div>

      <RewriteModal
        isOpen={showRewriteModal}
        selectedText={selectedText}
        onClose={() => { setShowRewriteModal(false); setSelectedText(''); }}
        onAccept={handleRewriteAccept}
      />
    </Layout>
  );
}
